import { useState, useEffect } from "react";
import { ensureMsalInitialized, getMsalInstance } from "../auth/msalClient";

/**
 * Hook that fetches the signed-in user's Microsoft profile photo directly
 * from Microsoft Graph using a User.Read token acquired via MSAL.
 * This avoids the backend OBO flow — profile photos are the user's own data.
 * Returns a blob URL for the photo or null if unavailable.
 * Automatically cleans up the blob URL on unmount.
 */
export function useProfilePhoto(): string | null {
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let blobUrl: string | null = null;

    (async () => {
      try {
        await ensureMsalInitialized();
        const pca = getMsalInstance();
        const account = pca.getActiveAccount() ?? pca.getAllAccounts()[0];
        if (!account) return;

        // Acquire a Graph token with User.Read (usually already consented)
        const result = await pca.acquireTokenSilent({
          scopes: ["User.Read"],
          account,
        });

        const res = await fetch("https://graph.microsoft.com/v1.0/me/photo/$value", {
          headers: { Authorization: `Bearer ${result.accessToken}` },
        });
        if (!res.ok) return;

        const blob = await res.blob();
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setPhotoUrl(blobUrl);
      } catch {
        // Silently fail — avatar will show initials fallback
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, []);

  return photoUrl;
}
